import React, { useState, useEffect, useContext } from "react";
import Select from "react-select";
import { JobFormContext } from "./JobFormContext";

interface Skill {
  id: number;
  name: string;
  category?: string;
  color?: string;
}

interface SkillsProps {
  skills: Skill[];
  skill_categories: string[];
  tabIndexFunc: (index: number) => void;
}

export default function Skills({
  skills,
  skill_categories,
  tabIndexFunc,
}: SkillsProps) {
  const [jobState, setJobState] = useContext(JobFormContext);
  const [skillState, setSkillState] = useState<number[]>(jobState.skill_ids || []);

  // useEffect, if skillState doesn't include 55, remove any skills with category "Language Proficiency (other than English)"
  useEffect(() => {
    const cSkills = skills.filter(skill => skill.category === "Language Proficiency (other than English)");
    // Check if skillState includes any of cSkills.id
    if (!skillState.includes(55) && skillState.some(id => cSkills.map(skill => skill.id).includes(id))) {
      // If yes, remove them
      setSkillState(skillState.filter(id => !cSkills.map(skill => skill.id).includes(id)));
    }
  }, [skillState, skills]);

  useEffect(() => {
    setJobState({ ...jobState, skill_ids: skillState });
  }, [skillState]);

  const customStyles = {
    option: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => {
      return {
        ...styles,
      };
    },
    multiValueLabel: (styles: any, { data }: any) => {
      return {
        backgroundColor: data.color,
        color: 'white',
        borderRadius: '5px 0 0 5px',
        paddingLeft: 8,
        paddingTop: 5,
        paddingBottom: 5,
        paddingRight: 3,
      };
    },
    multiValueRemove: (styles: any, { data }: any) => {
      return {
        ...styles,
        backgroundColor: data.color,
        color: 'white',
        borderRadius: '0 5px 5px 0',
        paddingRight: 5,
        paddingLeft: 4,
        marginLeft: -1,
      };
    },
  };

  return (
    <div className="job-builder-step job-builder-step--skills">
      <div className="job-builder-step__summary">
        <div>
          <strong>Choose only what matters</strong>
          <p>Candidates can quickly compare their experience with your requirements.</p>
        </div>
        <span>{skillState.length} selected</span>
      </div>
      <div className="job-builder-skill-groups">
        {skill_categories.map((category, categoryIndex) => {
          const category_skills = skills.filter(skill => skill.category === category);

          // Skip if category.name == "Language Proficiency (other than English)" & skill with id of 55 is not in job_skills
          const category_lang = category === "Language Proficiency (other than English)" &&
            !skillState.includes(55);

          if (category_lang) {
            return null;
          }

          const select_skills = category_skills.sort((a, b) => a.name.localeCompare(b.name))
            .map(skill => ({
              value: skill.id,
              label: skill.name,
              color: skill.color || '#6c757d' // Default color if missing
            }));

          return (
            <div key={categoryIndex} className="job-builder-skill-group">
              <h4>{category}</h4>

              <Select
                styles={customStyles}
                options={select_skills}
                classNamePrefix="react-select"
                isMulti
                value={select_skills.filter(skill => skillState.includes(skill.value))}
                onChange={(selected) => {
                  if (!selected) {
                    // Handle case where all items are removed
                    const unselected_skills = category_skills;
                    setSkillState(sS => sS.filter(s => !unselected_skills.map(u => u.id).includes(s)));
                    return;
                  }

                  // Get selected_skills that match selected
                  const selected_skills = category_skills.filter(skill =>
                    selected.map(s => s.value).includes(skill.id)
                  );

                  // Get category_skills that don't match selected skills
                  const unselected_skills = category_skills.filter(skill =>
                    !selected.map(s => s.value).includes(skill.id)
                  );

                  // Add new skills to skillState
                  setSkillState(sS => ([
                    ...sS,
                    ...selected_skills.map(s => s.id).filter(s => !sS.includes(s))
                  ]));

                  // Remove unselected skills from skillState
                  setSkillState(sS => sS.filter(s => !unselected_skills.map(u => u.id).includes(s)));
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="job-builder-actions">
        <button
          type="button"
          className="job-builder-button job-builder-button--secondary"
          onClick={() => {
            tabIndexFunc(0);
          }}
        >
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
          Back
        </button>
        <button
          type="button"
          className="job-builder-button job-builder-button--primary"
          onClick={() => {
            tabIndexFunc(2);
          }}
        >
          Continue to compensation
          <i className="fa fa-arrow-right" aria-hidden="true"></i>
        </button>
      </div>
    </div>
  );
}
